import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import useElementSize from "../hooks/use-element-size";
import {
  PILL_BORDER_DEFAULTS,
  buildPillPath,
  buildRectPath,
} from "./pill-border-utils";
import useAccessibilityContext from "../accessibility/store/use-accessibility-context";

/**
 * DashedBorder
 *
 * Renders an absolutely-positioned overlay that draws an animated dashed SVG
 * border and, optionally, a blurred backdrop behind its sibling elements.
 *
 * Place it as the FIRST child inside a `position: relative` container. The
 * component measures its own size via a ResizeObserver and redraws the SVG
 * path to always fit the container exactly.
 *
 * ─── BASIC USAGE ────────────────────────────────────────────────────────────
 *
 *   <div style={{ position: "relative" }}>
 *     <DashedBorder />
 *     <span>Label</span>
 *   </div>
 *
 * Hover or focus-visible on the parent makes the dashes march (see _controls.scss
 * `*:hover > .dashed-border path { animation-play-state: running }`).
 *
 * ─── pill PROP ───────────────────────────────────────────────────────────────
 *
 * By default (`pill={true}`) the overlay has `borderRadius: "9999px"`, giving
 * it a fully rounded pill shape that clips both the SVG border and the backdrop.
 * Set `pill={false}` for rectangular containers (cards, sections, etc.) where
 * rounded clipping is unwanted.
 *
 *   <DashedBorder pill={false} />
 *
 * ─── blurredBackdrop PROP ────────────────────────────────────────────────────
 *
 * When `blurredBackdrop={true}` the overlay gets the CSS class `blurred-backdrop`
 * (styled in _controls.scss), which adds:
 *   - `background-color: var(--menu-background)` — semi-transparent tint
 *   - `backdrop-filter: blur(13px)` — frosted-glass blur of page content behind it
 *
 * For the blur to be visible, the parent container must NOT have its own opaque
 * background — the background is provided exclusively by this component's span.
 *
 *   <div style={{ position: "relative" }}>
 *     <DashedBorder blurredBackdrop />
 *     <nav style={{ position: "relative", zIndex: 1 }}>...</nav>
 *   </div>
 *
 * The `.dashed-border ~ *` CSS rule in _controls.scss automatically applies
 * `position: relative; z-index: 1` to all following siblings, so the explicit
 * z-index above is usually not necessary.
 *
 * ─── simpleDesign PROP ──────────────────────────────────────────────────────
 *
 * When `simpleDesign={true}` the SVG is skipped entirely and a plain CSS
 * `border: 1px solid currentColor` is applied instead. Use this to respect
 * a reduced-decoration accessibility mode:
 *
 *   <DashedBorder simpleDesign={simpleDesign} />
 *
 * ─── CUSTOM DASH PATTERN ────────────────────────────────────────────────────
 *
 * Override the default stroke-dasharray/dashoffset to change the gap rhythm:
 *
 *   <DashedBorder dash="10 5" dashOffset={0} strokeWidth={1} />
 */
const DashedBorder = ({
  dash = PILL_BORDER_DEFAULTS.dash,
  dashOffset = PILL_BORDER_DEFAULTS.dashOffset,
  strokeWidth = PILL_BORDER_DEFAULTS.strokeWidth,
  blurredBackdrop = false,
  pill = true,
  reverse = false,
  simpleDesign: simpleDesignProp = false,
}) => {
  const { simpleDesign: simpleDesignCtx = false } =
    useAccessibilityContext() || {};
  const simpleDesign = simpleDesignProp || simpleDesignCtx;
  const [ref, { width: W, height: H }] = useElementSize();

  const d =
    W > 0 && H > 0
      ? pill
        ? buildPillPath(W, H, strokeWidth)
        : buildRectPath(W, H, strokeWidth)
      : null;

  return (
    <span
      ref={ref}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 0,
        borderRadius: pill === true ? "9999px" : "0",
        overflow: "hidden",
      }}
      className={classNames("dashed-border", {
        "blurred-backdrop": blurredBackdrop,
        pill: pill,
        reverse: reverse,
      })}
    >
      {d && (
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            overflow: "visible",
          }}
          aria-hidden="true"
        >
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            {...(!simpleDesign && {
              strokeDasharray: dash,
              strokeDashoffset: dashOffset,
            })}
            d={d}
          />
        </svg>
      )}
    </span>
  );
};

DashedBorder.propTypes = {
  dash: PropTypes.string,
  dashOffset: PropTypes.number,
  strokeWidth: PropTypes.number,
  blurredBackdrop: PropTypes.bool,
  pill: PropTypes.bool,
  reverse: PropTypes.bool,
  simpleDesign: PropTypes.bool,
};

export default DashedBorder;
